import { useMemo } from 'react';

import {
  type VideaNotesApiAdapter,
  type VideaNotesApiConfig,
  VideaNotesApiProvider,
  createVideaNotesApiClient,
} from '@repo/videa-notes';
import { shallowEqual, useSelector } from 'react-redux';

import { useHandleLogout } from '@/app/appHeader/utils';
import config from '@/config';
import { oktaReactQueryAxiosInstance } from '@/react-query/api';
import { getAccessToken } from '@/utils/api';

type LoginState = {
  login?: {
    user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
  };
};

/**
 * Builds the Videa Notes API adapter from Redux (practice/org) and client config.
 * Must be rendered inside Redux Provider so useSelector has access to login state.
 */
export const VideaNotesApiAdapterProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const { logout } = useHandleLogout();
  const { practiceId, organizationId } = useSelector(
    (state: LoginState) => ({
      practiceId: state?.login?.user?.primaryPracticeId ?? '',
      organizationId: state?.login?.user?.primaryOrganizationId ?? '',
    }),
    shallowEqual,
  );

  const adapter = useMemo<VideaNotesApiAdapter>(() => {
    const apiConfig: VideaNotesApiConfig = {
      getBaseUrl: () => config.API_URL,
      getAuthHeaders: async () => {
        const token = getAccessToken();
        return {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        };
      },
      getContext: () => ({ practiceId, organizationId }),
      onAuthError: () => void logout(undefined, { logoutType: 'FORCED' }),
      axios: oktaReactQueryAxiosInstance,
    };

    return createVideaNotesApiClient(apiConfig);
  }, [practiceId, organizationId, logout]);

  return (
    <VideaNotesApiProvider adapter={adapter}>{children}</VideaNotesApiProvider>
  );
};
